<template>
  <DetailCard title="Dados Profissionais">
    <template #actions>
      <button
        data-testid="transfer-crm-button"
        class="text-primary hover:underline text-sm font-medium whitespace-nowrap"
        @click="emit('transferCrm')"
      >
        Adicionar/Transferir CRM
      </button>
    </template>
    <div class="flex flex-col gap-5">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <div class="flex items-start gap-3">
          <v-icon size="20" color="grey-darken-1" class="mt-0.5">{{ mdiAccountOutline }}</v-icon>
          <div class="min-w-0">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-500">
              Nome registrado no CRM
            </p>
            <p data-testid="doctor-crm-name" class="text-base text-gray-900 break-words">
              {{ crmName || '—' }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-3">
          <v-icon size="20" color="grey-darken-1" class="mt-0.5">
            {{ mdiCardAccountDetailsOutline }}
          </v-icon>
          <div>
            <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Número do CRM</p>
            <p data-testid="doctor-crm-number" class="text-base text-gray-900">
              {{ doctor.doctor.mainCrm.number }}-{{ doctor.doctor.mainCrm.uf }}
            </p>
          </div>
        </div>

        <div v-if="doctor.doctor.mainCrm.status" class="flex items-start gap-3">
          <v-icon size="20" color="grey-darken-1" class="mt-0.5">
            {{ mdiCheckDecagramOutline }}
          </v-icon>
          <div>
            <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Status do CRM</p>
            <p class="text-base text-gray-900">{{ doctor.doctor.mainCrm.status }}</p>
          </div>
        </div>

        <div v-if="doctor.doctor.mainCrm.type" class="flex items-start gap-3">
          <v-icon size="20" color="grey-darken-1" class="mt-0.5">
            {{ mdiBadgeAccountOutline }}
          </v-icon>
          <div>
            <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Tipo do CRM</p>
            <p class="text-base text-gray-900">{{ doctor.doctor.mainCrm.type }}</p>
          </div>
        </div>
      </div>

      <!--
        Modelo 1 doctor : N doctor_crm (transferência de UF): a lista completa
        só aparece quando há mais de um CRM — com um único, os campos acima já
        contam a história toda.
      -->
      <div v-if="hasMultipleCrms" class="pt-5 border-t border-gray-100">
        <p class="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">CRMs do médico</p>
        <ul class="flex flex-col gap-2">
          <li
            v-for="crm in crms"
            :key="crm.id"
            data-testid="doctor-crm-item"
            class="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-3 py-2"
          >
            <span class="text-sm font-medium text-gray-900">{{ crm.number }}-{{ crm.uf }}</span>
            <span
              v-if="crm.isMain"
              data-testid="doctor-crm-main-badge"
              class="inline-flex items-center rounded-full bg-primary5 border border-primary px-2 py-0.5 text-xs font-medium text-primary"
            >
              Principal
            </span>
            <span v-if="crm.status" class="text-xs text-gray-500 ml-auto">{{ crm.status }}</span>
          </li>
        </ul>
      </div>

      <div v-if="hasSpecialties" class="pt-5 border-t border-gray-100">
        <p class="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">Especialidades</p>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="doctorSpecialty in doctor.doctor.mainCrm.doctorSpecialties"
            :key="doctorSpecialty.id"
            class="inline-flex items-center gap-2 rounded-lg border border-primary bg-primary5 px-3 py-1.5"
          >
            <v-icon size="16" color="primary">{{ mdiStethoscope }}</v-icon>
            <span class="text-sm font-medium text-textPrimary">
              {{ doctorSpecialty.specialty.name }}
            </span>
            <span
              v-if="doctorSpecialty.rqe"
              class="text-xs text-primary/80 border-l border-primary/30 pl-2"
            >
              RQE {{ doctorSpecialty.rqe }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </DetailCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  mdiAccountOutline,
  mdiCardAccountDetailsOutline,
  mdiCheckDecagramOutline,
  mdiBadgeAccountOutline,
  mdiStethoscope
} from '@mdi/js'

import type { DoctorDetailResponse } from '@/Network/Types/Responses/CompanyResponses'
import type { DoctorCrmResponse } from '@/Network/Types/Responses/DoctorCrmResponses'
import DetailCard from '../atoms/DetailCard.vue'
import { toTitleCase } from '../../helpers/doctorStatus'

const props = withDefaults(
  defineProps<{
    doctor: DoctorDetailResponse
    crms?: DoctorCrmResponse[]
  }>(),
  { crms: () => [] }
)

const emit = defineEmits<{ transferCrm: [] }>()

const crmName = computed(() => toTitleCase(props.doctor.doctor.mainCrm.name))

const hasMultipleCrms = computed(() => props.crms.length > 1)

const hasSpecialties = computed(
  () =>
    props.doctor.doctor.mainCrm.doctorSpecialties &&
    props.doctor.doctor.mainCrm.doctorSpecialties.length > 0
)
</script>
